import React, { FC } from 'react';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import styled from 'styled-components';
import { Icon } from '../icon';
import { ZTooltip } from '../ztooltip';

// ── Styled components ──────────────────────────────────────────

const MenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 200px;
    width: 200px;
    position: fixed;
    margin-top: 35px;
    height: fit-content;
`;

const MenuLinkContainer = styled.div<{ $active?: boolean }>`
    width: 100%;
    padding: 6px 9px 6px 25px;
    background: ${(props) => (props.$active ? 'rgb(0 0 0 / 5%)' : 'transparent')};
    border-radius: ${(props) => (props.$active ? '8px' : 'unset')};
    margin-bottom: 6px;
    display: flex;
    align-items: center;

    &:hover {
        background: rgb(0 0 0 / 5%);
        border-radius: 8px;
    }
`;

const MenuLink = styled.button`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    border: none;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    background: transparent;
    padding: 0;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SubMenuContainer = styled.div`
    padding-left: 12px;
`;

const SubMenuLink = styled.button`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: none;
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 100%;
    background: transparent;
    padding: 0;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const MenuItemContent = styled.span`
    flex: 1;
`;

const WarningIcon = styled(Icon)`
    color: #de1c22;
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    svg {
        width: 14px;
        height: 14px;
    }
`;

const ErrorsList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    ul {
        margin: 0;
        padding: 0;
        list-style-type: none;
    }

    li {
        font-size: 12px;
        margin-bottom: 2px;
        text-align: left;
    }
`;

export const PageMenuContentContainer = styled.div`
    flex: 1;
    min-width: 0;
    margin-left: 216px;
`;

export const PageMenuLayoutContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
    height: 100%;
`;

export const PageMenuSectionAnchor = styled.div`
    scroll-margin-top: 80px;
`;


// ── Types ──────────────────────────────────────────────────────

export interface ValidationError {
    fieldName: string;
    message: string;
}

export interface PageMenuSection {
    key: string;
    label: string;
    errors?: ValidationError[];
    subSections?: PageMenuSection[];
}

// ── Component ──────────────────────────────────────────────────

interface PageMenuProps {
    activeSection: string;
    activeSubSection?: string | null;
    sections: PageMenuSection[];
    onSectionClick: (sectionKey: string) => void;
    onSubSectionClick?: (subSectionKey: string) => void;
}

const ErrorIndicator: FC<{ errors: ValidationError[] }> = ({ errors }) => (
    <ZTooltip
        html={
            <ErrorsList>
                <ul>
                    {errors.map((error, index) => (
                        <li key={index}>
                            <strong>{error.fieldName}:</strong> {error.message}
                        </li>
                    ))}
                </ul>
            </ErrorsList>
        }
    >
        <WarningIcon>
            <InfoCircleIcon />
        </WarningIcon>
    </ZTooltip>
);

export const PageMenu: FC<PageMenuProps> = ({ activeSection, activeSubSection, sections, onSectionClick, onSubSectionClick }) => {
    return (
        <MenuContainer>
            {sections.map((section) => (
                <div key={section.key}>
                    <MenuLink onClick={() => onSectionClick(section.key)}>
                        <MenuLinkContainer $active={activeSection === section.key && !activeSubSection}>
                            <MenuItemContent>{section.label}</MenuItemContent>
                            {section.errors && section.errors.length > 0 && <ErrorIndicator errors={section.errors} />}
                        </MenuLinkContainer>
                    </MenuLink>

                    {section.subSections && onSubSectionClick && (
                        <SubMenuContainer>
                            {section.subSections.map((sub) => (
                                <SubMenuLink key={sub.key} onClick={() => onSubSectionClick(sub.key)}>
                                    <MenuLinkContainer $active={activeSubSection === sub.key}>
                                        <MenuItemContent>{sub.label}</MenuItemContent>
                                        {sub.errors && sub.errors.length > 0 && <ErrorIndicator errors={sub.errors} />}
                                    </MenuLinkContainer>
                                </SubMenuLink>
                            ))}
                        </SubMenuContainer>
                    )}
                </div>
            ))}
        </MenuContainer>
    );
};

export default PageMenu;
